/**
 * ScoreComparisonPanel
 * Panel showing score comparison (locked vs current).
 * Replaces the old tiny inline comparison badges in ScoreBadge.
 */

import type { ParlayScore } from '@hooks/useStrategyStats';
import type { TrackedParlay } from '@hooks/useTrackedParlays';
import { getParlayLabel, normalizeScoreForDisplay } from '@parlay-picker/shared/scoreDisplay';
import { cn } from '@utils/cn';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { type ReactElement, useMemo } from 'react';

// =============================================================================
// Types
// =============================================================================

interface ScoreComparisonPanelProps {
  parlay: TrackedParlay;
  strategyScore: ParlayScore | null;
  predictionScore: ParlayScore | null;
}

interface ScoreCardData {
  label: string;
  icon: string;
  lockedScore: number | null;
  lockedRec: string | null;
  /** The verdict stored when the parlay was tracked. */
  lockedTrusted: boolean | null | undefined;
  currentScore: number | null;
  currentRec: string | null;
  currentTrusted: boolean | null | undefined;
}

// =============================================================================
// Helpers
// =============================================================================

function recLabel(rec: string | null | undefined): string {
  if (!rec) return '';
  const upper = rec.toUpperCase();
  if (upper === 'BET') return 'BET';
  if (upper === 'LEAN BET') return 'LEAN';
  if (upper === 'CAUTION') return 'CAUTION';
  if (upper === 'SKIP') return 'SKIP';
  return upper;
}

function recColor(rec: string | null | undefined): string {
  if (!rec) return '';
  const upper = rec.toUpperCase();
  if (upper === 'BET') return 'text-theme-success';
  if (upper === 'LEAN BET') return 'text-teal-400';
  if (upper === 'CAUTION') return 'text-theme-warning';
  if (upper === 'SKIP') return 'text-theme-danger';
  if (upper === 'NO SIGNAL') return 'text-slate-400';
  return 'text-theme-text-secondary';
}

function deltaColor(delta: number): string {
  if (delta > 0) return 'text-theme-success';
  if (delta < 0) return 'text-theme-danger';
  return 'text-theme-text-secondary';
}

// =============================================================================
// Sub-components
// =============================================================================

function ScoreCard({
  data,
  thresholds,
}: {
  data: ScoreCardData;
  thresholds?: { bet: number; skip: number };
}): ReactElement {
  const hasLocked = data.lockedScore != null;
  const hasCurrent = data.currentScore != null;

  if (!hasLocked && !hasCurrent) {
    return (
      <div className={cn('flex-1 rounded-lg p-3 text-center', 'bg-theme-surface-hover')}>
        <div className="text-xs text-theme-text-tertiary">No data</div>
      </div>
    );
  }

  const normalizedLocked = hasLocked
    ? normalizeScoreForDisplay(data.lockedScore!, thresholds)
    : null;
  const normalizedCurrent = hasCurrent
    ? normalizeScoreForDisplay(data.currentScore!, thresholds)
    : null;
  const delta =
    normalizedLocked != null && normalizedCurrent != null
      ? normalizedCurrent - normalizedLocked
      : 0;
  const DeltaIcon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;

  // Derive display recommendations from normalized scores
  const lockedDisplayRec =
    normalizedLocked != null
      ? getParlayLabel(normalizedLocked, data.lockedTrusted).label
      : data.lockedRec;
  const currentDisplayRec =
    normalizedCurrent != null
      ? getParlayLabel(normalizedCurrent, data.currentTrusted).label
      : data.currentRec;

  return (
    <div className={cn('flex-1 rounded-lg p-2.5', 'bg-theme-surface-hover')}>
      <div className="text-[10px] text-theme-text-secondary mb-1.5">
        {data.icon} {data.label}
      </div>

      {/* Locked vs Current — always show both columns with labels */}
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-wider text-theme-text-tertiary mb-0.5">
            Locked
          </div>
          <span className="text-sm font-bold text-theme-text">
            {normalizedLocked != null ? normalizedLocked : '—'}
          </span>
          {lockedDisplayRec && (
            <span className={cn('ml-1 text-[10px] font-medium', recColor(lockedDisplayRec))}>
              {recLabel(lockedDisplayRec)}
            </span>
          )}
        </div>
        <span className="text-theme-text-tertiary text-xs">&rarr;</span>
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-wider text-theme-text-tertiary mb-0.5">
            Now
          </div>
          <span className="text-sm font-bold text-theme-text">
            {normalizedCurrent != null ? normalizedCurrent : '—'}
          </span>
          {currentDisplayRec && (
            <span className={cn('ml-1 text-[10px] font-medium', recColor(currentDisplayRec))}>
              {recLabel(currentDisplayRec)}
            </span>
          )}
        </div>
      </div>

      {/* Delta */}
      {normalizedLocked != null && normalizedCurrent != null && delta !== 0 && (
        <div className={cn('flex items-center gap-1 text-[10px] font-medium', deltaColor(delta))}>
          <DeltaIcon className="w-2.5 h-2.5" />
          {delta > 0 ? '+' : ''}
          {delta}
        </div>
      )}
      {normalizedLocked != null && normalizedCurrent != null && delta === 0 && (
        <div className="flex items-center gap-1 text-[10px] text-theme-text-tertiary">
          <Minus className="w-2.5 h-2.5" />
          No change
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ScoreComparisonPanel({
  parlay,
  strategyScore,
  predictionScore,
}: ScoreComparisonPanelProps): ReactElement | null {
  const strategyCard: ScoreCardData = useMemo(
    () => ({
      label: 'Your Score',
      icon: '💰',
      lockedScore: parlay.trackedStrategyScore ?? null,
      lockedRec: parlay.trackedStrategyRec ?? null,
      lockedTrusted: parlay.trackedLabelTrusted,
      currentScore: strategyScore?.score ?? null,
      currentRec: strategyScore?.recommendation ?? null,
      currentTrusted: strategyScore?.isLabelTrusted,
    }),
    [parlay, strategyScore],
  );

  const predictionCard: ScoreCardData = useMemo(
    () => ({
      label: 'AI Score',
      icon: '🎯',
      lockedScore: parlay.trackedPredictionScore ?? null,
      lockedRec: parlay.trackedPredictionRec ?? null,
      lockedTrusted: parlay.trackedLabelTrusted,
      currentScore: predictionScore?.score ?? null,
      currentRec: predictionScore?.recommendation ?? null,
      currentTrusted: predictionScore?.isLabelTrusted,
    }),
    [parlay, predictionScore],
  );

  const hasAnyScore =
    strategyCard.lockedScore != null ||
    strategyCard.currentScore != null ||
    predictionCard.lockedScore != null ||
    predictionCard.currentScore != null;

  if (!hasAnyScore) return null;

  return (
    <div
      className={cn('rounded-lg border p-2.5 mt-2', 'bg-theme-surface border-theme-border')}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[10px] font-medium text-theme-text-secondary mb-1.5">
        Score Comparison
      </div>

      {/* Score cards side by side — uses calibrated default thresholds */}
      <div className="flex gap-2 mb-1.5">
        <ScoreCard data={strategyCard} />
        <ScoreCard data={predictionCard} />
      </div>
    </div>
  );
}
